import * as THREE from 'three';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default class WaveImage {
  constructor(element) {
    this.element = element;
    this.image = element.querySelector('img');

    this.init();
  }

  init() {
    if (this.image.complete) {
      this.start();
    } else {
      this.image.addEventListener('load', () => {
        this.start();
      });
    }
  }

  start() {
    this.getSize();
    this.createScene();
    this.createCamera();
    this.createRenderer();
    this.createMesh();
    this.initInteraction();
    this.render();
  }

  getSize() {
    const rect = this.image.getBoundingClientRect();

    this.width = rect.width;
    this.height = rect.height;
  }

  createScene() {
    this.scene = new THREE.Scene();
  }

  createCamera() {
    this.camera = new THREE.OrthographicCamera(
      -this.width / 2,
      this.width / 2,
      this.height / 2,
      -this.height / 2,
      0.1,
      10,
    );

    this.camera.position.z = 1;
  }

  createRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
    });

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    this.renderer.setSize(this.width, this.height, false);

    const canvas = this.renderer.domElement;

    canvas.style.width = `${this.width}px`;
    canvas.style.height = `${this.height}px`;

    this.element.appendChild(canvas);
  }

  createMesh() {
    const geometry = new THREE.PlaneGeometry(this.width, this.height, 64, 64);

    const texture = new THREE.TextureLoader().load(this.image.src, () => {
      this.render();
    });

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uTexture: {
          value: texture,
        },

        uStrength: {
          value: 0,
        },
      },

      vertexShader: `
        uniform float uStrength;

        varying vec2 vUv;

        void main() {
          vUv = uv;

          vec3 newPosition = position;

          vec2 centered =
            (uv - 0.5) * 2.0;

          float dist =
            length(centered);

          float influence =
            0.35 +
            (
              1.0 -
              smoothstep(
                0.0,
                1.45,
                dist
              )
            ) * 0.65;

          float rounded =
            pow(influence, 1.35);

          newPosition.x +=
            centered.x *
            rounded *
            uStrength *
            1.7;

          newPosition.y +=
            centered.y *
            rounded *
            uStrength *
            0.75;

          gl_Position =
            projectionMatrix *
            modelViewMatrix *
            vec4(newPosition, 1.0);
        }
      `,

      fragmentShader: `
        uniform sampler2D uTexture;

        varying vec2 vUv;

        void main() {
          gl_FragColor =
            texture2D(uTexture, vUv);
        }
      `,
    });

    this.mesh = new THREE.Mesh(geometry, this.material);

    this.scene.add(this.mesh);
  }

  initInteraction() {
    this.strength = {
      scroll: 0,
      hover: 0,
      current: 0,
    };

    // SCROLL
    ScrollTrigger.create({
      trigger: this.element,

      onUpdate: (self) => {
        const velocity = Math.abs(self.getVelocity());

        this.strength.scroll = -gsap.utils.clamp(0, 90, velocity / 28);
      },
    });

    // HOVER
    this.element.addEventListener('mouseenter', () => {
      this.strength.hover = -45;
    });

    this.element.addEventListener('mouseleave', () => {
      this.strength.hover = 0;
    });

    // ANIMATION CONTINUE
    gsap.ticker.add(() => {
      // Le scroll revient tranquillement à zéro
      this.strength.scroll *= 0.94;

      // On additionne les deux influences
      const target = this.strength.scroll + this.strength.hover;

      // Lissage du mouvement
      this.strength.current += (target - this.strength.current) * 0.07;

      this.material.uniforms.uStrength.value = this.strength.current;

      this.render();
    });
  }
  render() {
    this.renderer.render(this.scene, this.camera);
  }
}
